import { z } from "zod";

const hexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Use a six-digit HEX color, for example #0A0A0A.");

export const HomeThemeDetailsSchema = z.object({
  hero_rule_color: hexColor,
  hero_rule_enabled: z.boolean(),
  clients_heading_color: hexColor,
  press_section_bg: hexColor,
  press_section_border_color: hexColor,
  press_section_border_enabled: z.boolean(),
  press_eyebrow_color: hexColor,
  press_body_color: hexColor,
  press_logo_bg: hexColor,
  press_logo_border_color: hexColor,
  press_logo_border_enabled: z.boolean(),
  press_logo_plate_enabled: z.boolean(),
  cta_rule_color: hexColor,
  cta_rule_enabled: z.boolean(),
  cta_eyebrow_color: hexColor,
});

export type HomeThemeDetails = z.infer<typeof HomeThemeDetailsSchema>;

export const DEFAULT_HOME_THEME_DETAILS: HomeThemeDetails = {
  hero_rule_color: "#DDDEDC",
  hero_rule_enabled: false,
  clients_heading_color: "#0A0A0A",
  press_section_bg: "#F2F2F0",
  press_section_border_color: "#DDDEDC",
  press_section_border_enabled: true,
  press_eyebrow_color: "#0A0A0A",
  press_body_color: "#6C706F",
  press_logo_bg: "#FAFAF8",
  press_logo_border_color: "#DDDEDC",
  press_logo_border_enabled: false,
  press_logo_plate_enabled: false,
  cta_rule_color: "#DDDEDC",
  cta_rule_enabled: true,
  cta_eyebrow_color: "#0A0A0A",
};

export function normalizeHomeThemeDetails(input: unknown): HomeThemeDetails {
  const candidate = input && typeof input === "object"
    ? { ...DEFAULT_HOME_THEME_DETAILS, ...(input as Partial<HomeThemeDetails>) }
    : DEFAULT_HOME_THEME_DETAILS;
  const result = HomeThemeDetailsSchema.safeParse(candidate);
  return result.success ? result.data : DEFAULT_HOME_THEME_DETAILS;
}

export function buildHomeThemeDetailsStylesheet(input: unknown): string {
  const settings = normalizeHomeThemeDetails(input);
  const heroRuleHeight = settings.hero_rule_enabled ? "1px" : "0px";
  const pressBorderWidth = settings.press_section_border_enabled ? "1px" : "0px";
  const pressLogoBorderWidth = settings.press_logo_border_enabled ? "1px" : "0px";
  const ctaRuleWidth = settings.cta_rule_enabled ? "1px" : "0px";

  return `
/* Admin → Settings → Home visual details. */
html body .home-money-palette > section:nth-of-type(1) > .absolute.inset-x-0.top-0.h-px {
  height: ${heroRuleHeight} !important;
  background-color: ${settings.hero_rule_color} !important;
}

html body .home-money-palette > section:nth-of-type(3) > p {
  color: ${settings.clients_heading_color} !important;
  opacity: 1 !important;
}

html body .home-money-palette > section.site-home-press {
  background-color: ${settings.press_section_bg} !important;
  background-image: none !important;
  border-top-width: ${pressBorderWidth} !important;
  border-bottom-width: ${pressBorderWidth} !important;
  border-color: ${settings.press_section_border_color} !important;
}
html body .home-money-palette > section.site-home-press > div > div:first-child > p:first-child {
  color: ${settings.press_eyebrow_color} !important;
  opacity: 1 !important;
}
html body .home-money-palette > section.site-home-press > div > div:first-child > p:nth-child(2) {
  color: ${settings.press_body_color} !important;
}
html body .home-money-palette > section.site-home-press img {
  background-color: ${settings.press_logo_plate_enabled ? settings.press_logo_bg : "transparent"} !important;
  border-style: solid !important;
  border-width: ${pressLogoBorderWidth} !important;
  border-color: ${settings.press_logo_border_color} !important;
}

html body .home-money-palette > section:nth-of-type(12) > div > div.relative {
  border-top-width: ${ctaRuleWidth} !important;
  border-bottom-width: ${ctaRuleWidth} !important;
  border-color: ${settings.cta_rule_color} !important;
}
html body .home-money-palette > section:nth-of-type(12) > div > div.relative > .absolute.inset-x-0.top-0.h-px {
  height: ${ctaRuleWidth} !important;
  background-color: ${settings.cta_rule_color} !important;
}
html body .home-money-palette > section:nth-of-type(12) p[class*="uppercase"] {
  color: ${settings.cta_eyebrow_color} !important;
  opacity: 1 !important;
}
`;
}

export const HOME_THEME_DETAIL_FIELDS: ReadonlyArray<{
  key: keyof Pick<HomeThemeDetails,
    | "hero_rule_color"
    | "clients_heading_color"
    | "press_section_bg"
    | "press_section_border_color"
    | "press_eyebrow_color"
    | "press_body_color"
    | "press_logo_bg"
    | "press_logo_border_color"
    | "cta_rule_color"
    | "cta_eyebrow_color"
  >;
  group: "Header and clients" | "Press coverage" | "Final CTA";
  label: string;
  description: string;
}> = [
  { key: "hero_rule_color", group: "Header and clients", label: "Rule below main navigation", description: "The thin line at the top of the Home hero, directly below the fixed navigation." },
  { key: "clients_heading_color", group: "Header and clients", label: "Client-logo heading", description: "The uppercase heading above the DTC and B2B client logos." },
  { key: "press_section_bg", group: "Press coverage", label: "Press section background", description: "The full background behind Recognition and Coverage." },
  { key: "press_section_border_color", group: "Press coverage", label: "Press section borders", description: "Top and bottom lines around the complete press section." },
  { key: "press_eyebrow_color", group: "Press coverage", label: "Press eyebrow", description: "Recognition and Coverage label." },
  { key: "press_body_color", group: "Press coverage", label: "Press supporting text", description: "Paragraph below the press eyebrow." },
  { key: "press_logo_bg", group: "Press coverage", label: "Press logo-card background", description: "Plate behind Forbes, AP, La Vanguardia, Europa Press and TechBehemoths." },
  { key: "press_logo_border_color", group: "Press coverage", label: "Press logo-card border", description: "Outline around each press logo plate." },
  { key: "cta_rule_color", group: "Final CTA", label: "Final CTA rules", description: "Top and bottom lines around the final scoped-recommendation CTA." },
  { key: "cta_eyebrow_color", group: "Final CTA", label: "Final CTA eyebrow", description: "Start with the business requirement label." },
];
